import React, { createContext, useCallback, useContext, useReducer } from 'react';

import { ANALYTICS_FILTERS_KEYS } from 'constants/ProjectConstants';
import { createNewCohort, setFiltersToLS } from 'constants/helper';
import { localStorageKeys } from 'constants/localStorageKeys';

// creating a context for easy prop passing.

const defaultGoalAnalyticsContextState = {
  allCohorts: [],
  allGroupByKeys: [],
  allSplitByKeys: [],

  currentState: {
    selectedCohort: null,
    selectedGroupByKey: null,
    selectedSplitByKey: null,
    selectedTimeBoundPeriods: [],
    noData: false,
    errorData: false,
  },

  selectedView: {
    viewId: null,
    expandedRowDetails: {
      headerComponent: null,
      infoObj: null,
    },
  },
};

const GoalAnalyticsContext = createContext(defaultGoalAnalyticsContextState);

const otherActions = {
  INITIALIZE_STATE: 'INITIALIZE_STATE',
  CHANGE_VIEW: 'CHANGE_VIEW',
  SET_EXPANSION_VIEW_DETAILS: 'SET_EXPANSION_VIEW_DETAILS',
  CLEAR_EXPANSION_VIEW_DETAILS: 'CLEAR_EXPANSION_VIEW_DETAILS',
  SET_NO_DATA: 'SET_NO_DATA',
  SET_ERROR_DATA: 'SET_ERROR_DATA',
};

const filterActions = {
  SELECT_COHORT: 'SELECT_COHORT',
  SELECT_GROUPBY: 'SELECT_GROUPBY',
  SELECT_SPLIT_BY_KEY: 'SELECT_SPLIT_BY_KEY',
  SELECT_TIMEBOUND_PERIODS: 'SELECT_TIMEBOUND_PERIODS',
};

const cohortCreationActions = {
  SHOW_SIDEPANEL: 'SHOW_SIDEPANEL',
  HIDE_SIDEPANEL: 'HIDE_SIDEPANEL',
  ADD_COHORT: 'ADD_COHORT',
};

const filterActionsReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case filterActions.SELECT_COHORT:
      return {
        ...state,
        currentState: {
          ...state.currentState,
          selectedCohort: payload.cohort,
          noData: false,
          errorData: false,
        },
      };
    case filterActions.SELECT_GROUPBY:
      return {
        ...state,
        currentState: {
          ...state.currentState,
          selectedGroupByKey: payload.groupByKey,
          noData: false,
          errorData: false,
        },
        selectedView: {
          viewId: payload.groupByKey.keyName,
          expandedRowDetails: {
            headerComponent: null,
            infoObj: null,
          },
        },
      };
    case filterActions.SELECT_SPLIT_BY_KEY:
      return {
        ...state,
        currentState: {
          ...state.currentState,
          selectedSplitByKey: payload.splitBy,
          noData: false,
          errorData: false,
        },
      };

    case filterActions.SELECT_TIMEBOUND_PERIODS:
      return {
        ...state,
        currentState: {
          ...state.currentState,
          selectedTimeBoundPeriods: payload?.timeBounds ?? [],
          noData: false,
          errorData: false,
        },
      };
    default:
      return state;
  }
};

const cohortCreationActionsReducer = (state, action) => {
  const { type, payload } = action;
  switch (type) {
    case cohortCreationActions.ADD_COHORT: {
      const newCohortList = createNewCohort(state.allCohorts, payload);
      return {
        ...state,
        allCohorts: newCohortList,
        currentState: {
          ...state.currentState,
          selectedCohort: payload.cohort,
          noData: false,
          errorData: false,
        },
      };
    }
    default:
      return state;
  }
};

const combinedReducer = (state, action) => {
  const { type, payload } = action;

  if (filterActions[type]) {
    return filterActionsReducer(state, action);
  }
  if (cohortCreationActions[type]) {
    return cohortCreationActionsReducer(state, action);
  }
  switch (type) {
    case otherActions.INITIALIZE_STATE: {
      let sortedCohorts = [...payload.cohorts];
      sortedCohorts = sortedCohorts.sort((objA, objB) => {
        if (String(objA.name).toLowerCase() === 'last used') return -1;

        if (String(objB.name).toLowerCase() === 'last used') return 1;

        return 0;
      });
      return {
        ...state,
        allCohorts: sortedCohorts,
        allGroupByKeys: payload.groupByKeys,
        allSplitByKeys: payload.splitByKeys,
        currentState: {
          ...state.currentState,
          selectedCohort: payload.selectedCohort ?? payload.cohorts[0],
          selectedGroupByKey: payload.selectedGroupByKey ?? payload.groupByKeys[0],
          selectedSplitByKey: payload.selectedSplitByKey ?? payload.splitByKeys[0],

          noData: false,
          errorData: false,
        },
        selectedView: {
          viewId: payload.selectedGroupByKey?.keyName ?? payload.groupByKeys[0]?.keyName,
          expandedRowDetails: {
            headerComponent: null,
            infoObj: null,
          },
        },
      };
    }
    case otherActions.CHANGE_VIEW: {
      return {
        ...state,
        selectedView: {
          viewId: payload.viewId,
          expandedRowDetails: {
            headerComponent: null,
            infoObj: null,
          },
        },
        currentState: {
          ...state.currentState,
          noData: false,
          errorData: false,
        },
      };
    }
    case otherActions.SET_EXPANSION_VIEW_DETAILS: {
      return {
        ...state,
        selectedView: {
          ...state.selectedView,
          expandedRowDetails: {
            headerComponent: payload.headerComponent,
            infoObj: payload.infoObj,
          },
        },
        currentState: {
          ...state.currentState,
          noData: false,
          errorData: false,
        },
      };
    }
    case otherActions.CLEAR_EXPANSION_VIEW_DETAILS: {
      return {
        ...state,
        selectedView: {
          ...state.selectedView,
          expandedRowDetails: {
            headerComponent: null,
            infoObj: null,
          },
        },
        currentState: {
          ...state.currentState,
          noData: false,
          errorData: false,
        },
      };
    }
    case otherActions.SET_NO_DATA: {
      return {
        ...state,
        currentState: {
          ...state.currentState,
          noData: payload.noData,
        },
      };
    }
    case otherActions.SET_ERROR_DATA: {
      return {
        ...state,
        currentState: {
          ...state.currentState,
          errorData: payload.errorData,
        },
      };
    }
    default: {
      return state;
    }
  }
};

export const GoalAnalyticsProvider = ({ children }) => {
  const [state, goalAnalyticsDispatch] = useReducer(combinedReducer, defaultGoalAnalyticsContextState);

  const updateTimeBoundPeriods = useCallback((timeBounds) => {
    setFiltersToLS(localStorageKeys.analyticsGoalFilters, ANALYTICS_FILTERS_KEYS.timeBounds, timeBounds);
    goalAnalyticsDispatch({
      type: filterActions.SELECT_TIMEBOUND_PERIODS,
      payload: {
        timeBounds,
      },
    });
  }, []);

  const selectCohort = useCallback((cohort) => {
    setFiltersToLS(localStorageKeys.analyticsGoalFilters, ANALYTICS_FILTERS_KEYS.cohortId, cohort.id);
    goalAnalyticsDispatch({
      type: filterActions.SELECT_COHORT,
      payload: { cohort },
    });
  }, []);

  const selectGroupBy = useCallback((groupByKey) => {
    setFiltersToLS(localStorageKeys.analyticsGoalFilters, ANALYTICS_FILTERS_KEYS.groupByKey, groupByKey.keyName);
    goalAnalyticsDispatch({
      type: filterActions.SELECT_GROUPBY,
      payload: { groupByKey },
    });
  }, []);

  const selectSplitByKey = useCallback((splitBy) => {
    goalAnalyticsDispatch({
      type: filterActions.SELECT_SPLIT_BY_KEY,
      payload: { splitBy },
    });
  }, []);

  const initializeState = useCallback((initialValues) => {
    goalAnalyticsDispatch({
      type: otherActions.INITIALIZE_STATE,
      payload: initialValues,
    });
  }, []);

  // cohort creation functions.
  const addCohort = useCallback((cohort) => {
    setFiltersToLS(localStorageKeys.analyticsGoalFilters, ANALYTICS_FILTERS_KEYS.cohortId, cohort.uuid);
    goalAnalyticsDispatch({
      type: cohortCreationActions.ADD_COHORT,
      payload: { cohort },
    });
  }, []);

  // view change functions.
  const changeSelectedView = (viewId) => {
    goalAnalyticsDispatch({
      type: otherActions.CHANGE_VIEW,
      payload: { viewId },
    });
  };

  const setExpandedRowDetailsView = ({ headerComponent, infoObj }) => {
    goalAnalyticsDispatch({
      type: otherActions.SET_EXPANSION_VIEW_DETAILS,
      payload: {
        headerComponent,
        infoObj,
      },
    });
  };

  const clearExpandedRowDetailsView = () => {
    goalAnalyticsDispatch({
      type: otherActions.CLEAR_EXPANSION_VIEW_DETAILS,
    });
  };

  const setNoData = (noData) => {
    goalAnalyticsDispatch({
      type: otherActions.SET_NO_DATA,
      payload: {
        noData,
      },
    });
  };

  const setErrorData = (errorData) => {
    goalAnalyticsDispatch({
      type: otherActions.SET_ERROR_DATA,
      payload: {
        errorData,
      },
    });
  };

  return (
    <GoalAnalyticsContext.Provider
      value={{
        state,
        updateTimeBoundPeriods,
        selectCohort,
        selectGroupBy,
        selectSplitByKey,
        initializeState,
        addCohort,
        changeSelectedView,
        setExpandedRowDetailsView,
        clearExpandedRowDetailsView,
        setNoData,
        setErrorData,
      }}
    >
      {children}
    </GoalAnalyticsContext.Provider>
  );
};

const useGoalAnalytics = () => useContext(GoalAnalyticsContext);

export { useGoalAnalytics };
